import { useCallback, useRef, useEffect } from 'react';
import { getWorkerManager, areWorkersSupported } from './worker-manager';
import type { SearchableRequest, SearchFilter, SortConfig } from './types';

export type { SearchableRequest, SearchFilter, SortConfig };

function matchesFilterSync(
  request: SearchableRequest,
  filter: SearchFilter,
): boolean {
  if (filter.searchText) {
    const searchLower = filter.searchText.toLowerCase();
    const urlMatch = request.url.toLowerCase().includes(searchLower);
    const hostMatch = request.host.toLowerCase().includes(searchLower);
    const commentMatch = request.comment?.toLowerCase().includes(searchLower);
    const tagMatch = request.tags?.some((tag) =>
      tag.toLowerCase().includes(searchLower),
    );

    if (!urlMatch && !hostMatch && !commentMatch && !tagMatch) {
      return false;
    }
  }

  if (filter.methods && filter.methods.length > 0) {
    if (!filter.methods.includes(request.method)) {
      return false;
    }
  }

  if (filter.statusCodes) {
    if (request.status === undefined) {
      if (
        filter.statusCodes.min !== undefined ||
        filter.statusCodes.max !== undefined
      ) {
        return false;
      }
    } else {
      if (
        filter.statusCodes.min !== undefined &&
        request.status < filter.statusCodes.min
      ) {
        return false;
      }
      if (
        filter.statusCodes.max !== undefined &&
        request.status > filter.statusCodes.max
      ) {
        return false;
      }
    }
  }

  if (filter.hosts && filter.hosts.length > 0) {
    const hostLower = request.host.toLowerCase();
    const matches = filter.hosts.some((h) =>
      hostLower.includes(h.toLowerCase()),
    );
    if (!matches) {
      return false;
    }
  }

  if (filter.urlPattern) {
    const pattern = filter.urlPattern
      .replace(/[.+^${}()|[\]\\]/g, '\\$&')
      .replace(/\*/g, '.*');
    const regex = new RegExp(pattern, 'i');
    if (!regex.test(request.url)) {
      return false;
    }
  }

  if (filter.hasHighlight !== undefined) {
    const hasHighlight = !!request.highlight;
    if (filter.hasHighlight !== hasHighlight) {
      return false;
    }
  }

  if (filter.hasComment !== undefined) {
    const hasComment = !!request.comment && request.comment.trim().length > 0;
    if (filter.hasComment !== hasComment) {
      return false;
    }
  }

  if (filter.tags && filter.tags.length > 0) {
    if (!request.tags || request.tags.length === 0) {
      return false;
    }
    const hasAllTags = filter.tags.every((tag) =>
      request.tags?.includes(tag.toLowerCase()),
    );
    if (!hasAllTags) {
      return false;
    }
  }

  if (filter.dateRange) {
    if (
      filter.dateRange.start !== undefined &&
      request.timestamp < filter.dateRange.start
    ) {
      return false;
    }
    if (
      filter.dateRange.end !== undefined &&
      request.timestamp > filter.dateRange.end
    ) {
      return false;
    }
  }

  return true;
}

function sortRequestsSync(
  requests: SearchableRequest[],
  sort: SortConfig,
): SearchableRequest[] {
  const sorted = [...requests];

  sorted.sort((a, b) => {
    const aVal = a[sort.field];
    const bVal = b[sort.field];

    if (aVal === undefined && bVal === undefined) return 0;
    if (aVal === undefined) return sort.direction === 'asc' ? -1 : 1;
    if (bVal === undefined) return sort.direction === 'asc' ? 1 : -1;

    let comparison: number;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      comparison = aVal.localeCompare(bVal);
    } else if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal;
    } else {
      comparison = String(aVal).localeCompare(String(bVal));
    }

    return sort.direction === 'asc' ? comparison : -comparison;
  });

  return sorted;
}

function filterRequestsSync(
  requests: SearchableRequest[],
  filter: SearchFilter,
  sort?: SortConfig,
  limit?: number,
  offset?: number,
): { ids: string[]; totalCount: number } {
  let filtered = requests.filter((req) => matchesFilterSync(req, filter));
  const totalCount = filtered.length;

  if (sort) {
    filtered = sortRequestsSync(filtered, sort);
  }

  if (offset !== undefined && offset > 0) {
    filtered = filtered.slice(offset);
  }
  if (limit !== undefined && limit > 0) {
    filtered = filtered.slice(0, limit);
  }

  const ids = filtered.map((req) => req.id);
  return { ids, totalCount };
}

function searchRequestsSync(
  requests: SearchableRequest[],
  query: string,
  fields: (keyof SearchableRequest)[],
): {
  ids: string[];
  matches: { id: string; field: string; snippet: string }[];
} {
  const queryLower = query.toLowerCase();
  const matchingIds: string[] = [];
  const matches: { id: string; field: string; snippet: string }[] = [];

  for (const request of requests) {
    let hasMatch = false;

    for (const field of fields) {
      const value = request[field];
      if (value === undefined || value === null) continue;

      const stringValue = String(value);
      const stringLower = stringValue.toLowerCase();
      const matchIndex = stringLower.indexOf(queryLower);

      if (matchIndex !== -1) {
        hasMatch = true;

        const snippetStart = Math.max(0, matchIndex - 20);
        const snippetEnd = Math.min(
          stringValue.length,
          matchIndex + query.length + 20,
        );
        let snippet = stringValue.slice(snippetStart, snippetEnd);

        if (snippetStart > 0) snippet = '...' + snippet;
        if (snippetEnd < stringValue.length) snippet = snippet + '...';

        matches.push({
          id: request.id,
          field: field as string,
          snippet,
        });
      }
    }

    if (hasMatch) {
      matchingIds.push(request.id);
    }
  }

  return { ids: matchingIds, matches };
}

const WORKER_THRESHOLD = 100;

export type FilterResult = {
  ids: string[];
  totalCount: number;
};

export type SearchResult = {
  ids: string[];
  matches: { id: string; field: string; snippet: string }[];
};

export function useSearch() {
  const useWorkerRef = useRef(areWorkersSupported());
  const workerDataSyncedRef = useRef(false);
  const localDataRef = useRef<SearchableRequest[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const syncDataToWorker = useCallback(
    async (requests: SearchableRequest[]): Promise<void> => {
      localDataRef.current = requests;

      if (!useWorkerRef.current || requests.length < WORKER_THRESHOLD) {
        workerDataSyncedRef.current = false;
        return;
      }

      const manager = getWorkerManager();
      if (!manager) {
        workerDataSyncedRef.current = false;
        return;
      }

      try {
        await manager.sendMessage<
          { requests: SearchableRequest[] },
          { count: number }
        >('search', 'setData', { requests });
        workerDataSyncedRef.current = true;
      } catch (error) {
        console.warn('Failed to sync data to worker:', error);
        workerDataSyncedRef.current = false;
      }
    },
    [],
  );

  const addRequestsToWorker = useCallback(
    async (requests: SearchableRequest[]): Promise<void> => {
      localDataRef.current = [...localDataRef.current, ...requests];

      if (!useWorkerRef.current || !workerDataSyncedRef.current) {
        return;
      }

      const manager = getWorkerManager();
      if (!manager) return;

      try {
        await manager.sendMessage<
          { requests: SearchableRequest[] },
          { count: number }
        >('search', 'addRequests', { requests });
      } catch (error) {
        console.warn('Failed to add requests to worker:', error);
      }
    },
    [],
  );

  const updateRequestInWorker = useCallback(
    async (request: SearchableRequest): Promise<void> => {
      const idx = localDataRef.current.findIndex((r) => r.id === request.id);
      if (idx >= 0) {
        localDataRef.current[idx] = {
          ...localDataRef.current[idx],
          ...request,
        };
      } else {
        localDataRef.current.push(request);
      }

      if (!useWorkerRef.current || !workerDataSyncedRef.current) {
        return;
      }

      const manager = getWorkerManager();
      if (!manager) return;

      try {
        await manager.sendMessage<
          { request: SearchableRequest },
          { updated: boolean }
        >('search', 'updateRequest', { request });
      } catch (error) {
        console.warn('Failed to update request in worker:', error);
      }
    },
    [],
  );

  const removeRequestsFromWorker = useCallback(
    async (ids: string[]): Promise<void> => {
      const idSet = new Set(ids);
      localDataRef.current = localDataRef.current.filter(
        (r) => !idSet.has(r.id),
      );

      if (!useWorkerRef.current || !workerDataSyncedRef.current) {
        return;
      }

      const manager = getWorkerManager();
      if (!manager) return;

      try {
        await manager.sendMessage<{ ids: string[] }, { removed: number }>(
          'search',
          'removeRequests',
          { ids },
        );
      } catch (error) {
        console.warn('Failed to remove requests from worker:', error);
      }
    },
    [],
  );

  const clearWorkerData = useCallback(async (): Promise<void> => {
    localDataRef.current = [];
    workerDataSyncedRef.current = false;

    const manager = getWorkerManager();
    if (!manager) return;

    try {
      await manager.sendMessage<undefined, { cleared: boolean }>(
        'search',
        'clearData',
        undefined,
      );
    } catch {
      // Ignore errors on clear
    }
  }, []);

  const filterRequests = useCallback(
    async (
      filter: SearchFilter,
      sort?: SortConfig,
      limit?: number,
      offset?: number,
    ): Promise<FilterResult> => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      if (
        !workerDataSyncedRef.current ||
        localDataRef.current.length < WORKER_THRESHOLD
      ) {
        return filterRequestsSync(
          localDataRef.current,
          filter,
          sort,
          limit,
          offset,
        );
      }

      const manager = getWorkerManager();
      if (!manager) {
        return filterRequestsSync(
          localDataRef.current,
          filter,
          sort,
          limit,
          offset,
        );
      }

      try {
        const result = await manager.sendMessage<
          {
            filter: SearchFilter;
            sort?: SortConfig;
            limit?: number;
            offset?: number;
          },
          FilterResult
        >('search', 'filter', { filter, sort, limit, offset });

        if (signal.aborted) {
          return { ids: [], totalCount: 0 };
        }

        return result;
      } catch (error) {
        if (signal.aborted) {
          return { ids: [], totalCount: 0 };
        }
        console.warn(
          'Worker filterRequests failed, using sync fallback:',
          error,
        );
        return filterRequestsSync(
          localDataRef.current,
          filter,
          sort,
          limit,
          offset,
        );
      }
    },
    [],
  );

  const searchRequests = useCallback(
    async (
      query: string,
      fields: (keyof SearchableRequest)[] = ['url', 'host'],
    ): Promise<SearchResult> => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      if (!query.trim()) {
        return { ids: [], matches: [] };
      }

      if (
        !workerDataSyncedRef.current ||
        localDataRef.current.length < WORKER_THRESHOLD
      ) {
        return searchRequestsSync(localDataRef.current, query, fields);
      }

      const manager = getWorkerManager();
      if (!manager) {
        return searchRequestsSync(localDataRef.current, query, fields);
      }

      try {
        const result = await manager.sendMessage<
          { query: string; fields: (keyof SearchableRequest)[] },
          SearchResult
        >('search', 'search', { query, fields });

        if (signal.aborted) {
          return { ids: [], matches: [] };
        }

        return result;
      } catch (error) {
        if (signal.aborted) {
          return { ids: [], matches: [] };
        }
        console.warn(
          'Worker searchRequests failed, using sync fallback:',
          error,
        );
        return searchRequestsSync(localDataRef.current, query, fields);
      }
    },
    [],
  );

  return {
    syncData: syncDataToWorker,
    addRequests: addRequestsToWorker,
    updateRequest: updateRequestInWorker,
    removeRequests: removeRequestsFromWorker,
    clearData: clearWorkerData,
    filterRequests,
    searchRequests,
    filterRequestsSync,
    searchRequestsSync,
    matchesFilter: matchesFilterSync,
    isWorkerSupported: useWorkerRef.current,
    isDataSynced: workerDataSyncedRef.current,
    workerThreshold: WORKER_THRESHOLD,
  };
}

export { filterRequestsSync, searchRequestsSync, matchesFilterSync };
